import React from "react";
import { motion } from "framer-motion";
import { Award, GitBranch, Star, Network, Compass } from "lucide-react";

const APP_HERO_SHOT =
  "https://customer-assets.emergentagent.com/job_network-recognition/artifacts/9250v0vw_Screenshot%202026-06-03%20at%2011.25.41.png";
const APP_PROFILE =
  "https://customer-assets.emergentagent.com/job_network-recognition/artifacts/jn4r2mw4_Screenshot%202026-05-15%20at%2015.01.29.png";
const APP_COMPOSITE =
  "https://customer-assets.emergentagent.com/job_f8435c69-857e-497d-b480-eeab9bfacf99/artifacts/2puvmc6j_Screenshot%202026-05-14%20at%2010.52.45.png";

const FEATURES = [
  {
    id: "network",
    eyebrow: "Industry Network Mapping",
    title: "See The Rooms Behind The Records.",
    body:
      "Discover how people connect across projects, teams, studios, tours, and creative networks. Make the invisible structure of the industry visible.",
    icon: Network,
    image: APP_HERO_SHOT,
    mock: "network",
  },
  {
    id: "ratings",
    eyebrow: "Loudar Ratings & Reviews",
    title: "Reputation You Can Prove.",
    body:
      "Trusted reviews and industry-backed ratings highlight professionalism, reliability, talent, and consistency — across every room you walk into.",
    icon: Star,
    image: APP_COMPOSITE,
    mock: "ratings",
  },
  {
    id: "profiles",
    eyebrow: "Professional Profiles",
    title: "More Than A Bio.",
    body:
      "Professionally represented profiles that showcase your journey, verified collaborations, reviews, and career growth — in one cinematic identity layer.",
    icon: Award,
    image: APP_PROFILE,
    mock: "profile",
  },
  {
    id: "collabs",
    eyebrow: "Verified Collaborations",
    title: "Built From Real Work.",
    body:
      "Every collaboration strengthens credibility through real projects, real gigs, and real industry experience. Two-way confirmed — never claimed.",
    icon: GitBranch,
    image: APP_COMPOSITE,
    mock: "collabs",
  },
  {
    id: "discovery",
    eyebrow: "Career Discovery",
    title: "Find The Right Pathways.",
    body:
      "Understand who does what, how opportunities form, and where your next collaboration could come from. Open doors that once felt out of reach.",
    icon: Compass,
    image: APP_COMPOSITE,
    mock: "discovery",
  },
];

const ProfileMock = () => (
  <div className="relative aspect-[4/5] w-full max-w-md overflow-hidden rounded-md border border-white/10 bg-loudar-surface shadow-[0_30px_80px_-30px_rgba(255,107,26,0.35)]">
    <img
      src="https://images.unsplash.com/photo-1531123897727-8f129e1688ce?auto=format&fit=crop&w=900&q=80"
      alt="Artist"
      className="absolute inset-0 h-2/3 w-full object-cover"
    />
    <div className="absolute inset-x-0 top-1/2 h-1/2 bg-gradient-to-b from-loudar-ink/10 to-loudar-ink" />
    <div className="absolute inset-x-0 bottom-0 p-5">
      <div className="font-display text-4xl uppercase tracking-tight text-white">MARA J.</div>
      <div className="mt-1 flex flex-wrap gap-2">
        <span className="rounded-full bg-loudar-orange/90 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-loudar-ink">
          Producer
        </span>
        <span className="rounded-full bg-loudar-orange/90 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-loudar-ink">
          Engineer
        </span>
      </div>
      <div className="mt-3 flex items-center gap-4 text-xs text-white/70">
        <span className="font-mono text-loudar-orange">4.9</span>
        <span>· 42 verified collabs</span>
        <span>· London</span>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2">
        {["Stems", "Credits", "Reviews"].map((t) => (
          <div
            key={t}
            className="rounded-sm border border-white/10 bg-white/[0.04] py-2 text-center text-[10px] uppercase tracking-[0.16em] text-white/70"
          >
            {t}
          </div>
        ))}
      </div>
    </div>
  </div>
);

const CollabMock = () => (
  <div className="relative w-full max-w-md space-y-3">
    {[
      { t: "NIGHT BLOOM", role: "Mixed", who: "with K. ALMA", v: true },
      { t: "DUST & GOLD", role: "Engineered", who: "with WAVEHEAD", v: true },
      { t: "ON THE LOW", role: "Produced", who: "with M. CARTER", v: true },
      { t: "STILL ROOM", role: "Co-wrote", who: "with EVA RYE", v: false },
    ].map((c, i) => (
      <div
        key={i}
        className="flex items-center justify-between rounded-sm border border-white/10 bg-loudar-surface p-4"
      >
        <div>
          <div className="font-display text-xl uppercase tracking-tight">{c.t}</div>
          <div className="text-xs uppercase tracking-[0.18em] text-white/50">
            {c.role} · {c.who}
          </div>
        </div>
        {c.v ? (
          <span className="rounded-full bg-loudar-orange/15 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-loudar-orange">
            Verified
          </span>
        ) : (
          <span className="rounded-full border border-white/15 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-white/40">
            Pending
          </span>
        )}
      </div>
    ))}
  </div>
);

const RatingsMock = () => (
  <div className="w-full max-w-md space-y-4">
    {[
      { label: "Professionalism", v: 96 },
      { label: "Results", v: 88 },
      { label: "Communication", v: 100 },
      { label: "Creativity", v: 82 },
      { label: "Reliability", v: 94 },
    ].map((r, i) => (
      <div key={i}>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-xs uppercase tracking-[0.2em] text-white/70">{r.label}</span>
          <span className="font-mono text-xs text-loudar-orange">{r.v}/100</span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/[0.06]">
          <div
            className="h-full rounded-full bg-loudar-orange"
            style={{ width: `${r.v}%` }}
          />
        </div>
      </div>
    ))}
    <div className="rounded-sm border border-white/10 bg-loudar-surface p-4">
      <p className="text-sm italic text-white/80">
        "Showed up. Delivered. Made the room better. Would book again."
      </p>
      <p className="mt-2 text-[10px] uppercase tracking-[0.2em] text-white/40">
        — Tour Manager, sold-out UK run
      </p>
    </div>
  </div>
);

const NetworkMock = () => {
  const nodes = [
    { x: 250, y: 200, label: "YOU", rx: 50, ry: 26, primary: true },
    { x: 70, y: 70, label: "Producer", rx: 48, ry: 20 },
    { x: 430, y: 80, label: "Engineer", rx: 48, ry: 20 },
    { x: 50, y: 320, label: "Manager", rx: 48, ry: 20 },
    { x: 450, y: 320, label: "A&R", rx: 36, ry: 20 },
    { x: 250, y: 30, label: "DJ", rx: 32, ry: 18 },
    { x: 250, y: 370, label: "Videographer", rx: 60, ry: 20 },
    { x: 80, y: 200, label: "Photographer", rx: 60, ry: 20 },
    { x: 420, y: 200, label: "Label", rx: 36, ry: 20 },
  ];
  // edges between indices
  const edges = [
    [0, 1], [0, 2], [0, 3], [0, 4], [0, 5], [0, 6], [0, 7], [0, 8],
    [1, 2], [3, 4], [5, 2], [7, 6], [1, 7], [2, 8],
  ];
  return (
    <svg viewBox="0 0 500 400" className="w-full max-w-xl">
      <defs>
        <radialGradient id="g-net" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FF6B1A" stopOpacity="0.65" />
          <stop offset="100%" stopColor="#FF6B1A" stopOpacity="0" />
        </radialGradient>
      </defs>

      {edges.map(([a, b], i) => {
        const na = nodes[a];
        const nb = nodes[b];
        return (
          <line
            key={i}
            x1={na.x}
            y1={na.y}
            x2={nb.x}
            y2={nb.y}
            stroke="#FF6B1A"
            strokeOpacity={a === 0 || b === 0 ? 0.5 : 0.18}
            strokeWidth={a === 0 || b === 0 ? 1.2 : 1}
            strokeDasharray={a === 0 || b === 0 ? "0" : "3 4"}
          />
        );
      })}

      {nodes.map((n, i) => (
        <g key={i}>
          {n.primary && (
            <ellipse cx={n.x} cy={n.y} rx={n.rx * 1.8} ry={n.ry * 2.2} fill="url(#g-net)" />
          )}
          <ellipse
            cx={n.x}
            cy={n.y}
            rx={n.rx}
            ry={n.ry}
            fill={n.primary ? "#FF6B1A" : "#0E0E0E"}
            stroke={n.primary ? "#FF6B1A" : "#FFFFFF"}
            strokeOpacity={n.primary ? 1 : 0.55}
            strokeWidth="1.4"
          />
          <text
            x={n.x}
            y={n.y + 5}
            textAnchor="middle"
            fontSize="14"
            fontWeight="600"
            fill={n.primary ? "#050505" : "#fff"}
            fontFamily="'Cabinet Grotesk','Space Grotesk',sans-serif"
            style={{ letterSpacing: n.primary ? "0.18em" : "0.02em" }}
          >
            {n.primary ? n.label : n.label}
          </text>
        </g>
      ))}
    </svg>
  );
};

const DiscoveryMock = () => (
  <div className="w-full max-w-md space-y-3">
    {[
      { t: "Sessions in your city", n: 12, k: "London · this month" },
      { t: "Producers seeking vocalists", n: 38, k: "Hip-hop · R&B" },
      { t: "Studios with availability", n: 21, k: "Within 5 miles" },
      { t: "Tours hiring crew", n: 7, k: "Europe Q2" },
    ].map((d, i) => (
      <div
        key={i}
        className="flex items-center justify-between rounded-sm border border-white/10 bg-loudar-surface p-4"
      >
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-white/50">{d.k}</div>
          <div className="mt-1 text-base text-white">{d.t}</div>
        </div>
        <div className="font-display text-4xl text-loudar-orange">{d.n}</div>
      </div>
    ))}
  </div>
);

const MOCKS = {
  profile: <ProfileMock />,
  collabs: <CollabMock />,
  ratings: <RatingsMock />,
  network: <NetworkMock />,
  discovery: <DiscoveryMock />,
};

export const ProductShowcase = () => {
  return (
    <section
      id="features"
      data-testid="product-showcase-section"
      className="relative bg-loudar-ink py-24 md:py-32"
    >
      <div className="container-loudar">
        <div className="space-y-28 md:space-y-40">
          {FEATURES.map((f, idx) => {
            const Icon = f.icon;
            const reverse = idx % 2 === 1;
            return (
              <div
                key={f.id}
                data-testid={`feature-${f.id}`}
                className={`grid grid-cols-1 items-center gap-10 md:grid-cols-12 md:gap-16 ${
                  reverse ? "md:[&>div:first-child]:order-2" : ""
                }`}
              >
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.7 }}
                  className="md:col-span-6"
                >
                  <div className="mb-4 inline-flex items-center gap-3">
                    <span className="grid h-9 w-9 place-items-center rounded-sm border border-loudar-orange/40 bg-loudar-orange/10 text-loudar-orange">
                      <Icon size={16} />
                    </span>
                    <span className="label-mono">{f.eyebrow}</span>
                  </div>
                  <h3 className="font-display text-3xl uppercase leading-[0.95] tracking-tight text-white sm:text-4xl md:text-5xl">
                    {f.title}
                  </h3>
                  <p className="mt-5 max-w-lg text-base text-white/65 md:text-lg">{f.body}</p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8, delay: 0.1 }}
                  className="md:col-span-6"
                >
                  <div className="relative">
                    <div className="absolute -inset-6 -z-10 rounded-md bg-loudar-orange/10 blur-3xl" />
                    <div className="relative isolate flex items-center justify-center overflow-hidden rounded-md border border-white/10 bg-loudar-surface p-6 md:p-10 min-h-[480px]">
                      {/* Blurred real app screenshot as ambient backdrop */}
                      <img
                        src={f.image}
                        alt=""
                        aria-hidden
                        loading="lazy"
                        className="pointer-events-none absolute inset-0 -z-10 h-full w-full object-cover"
                        style={
                          f.id === "network"
                            ? { opacity: 0.95, filter: "saturate(115%)" }
                            : { opacity: 0.65, filter: "blur(14px) saturate(115%)" }
                        }
                      />
                      <div
                        aria-hidden
                        className={`pointer-events-none absolute inset-0 -z-10 ${
                          f.id === "network"
                            ? "bg-gradient-to-br from-loudar-ink/35 via-loudar-ink/15 to-loudar-ink/55"
                            : "bg-gradient-to-br from-loudar-ink/60 via-loudar-ink/45 to-loudar-ink/80"
                        }`}
                      />
                      {MOCKS[f.mock]}
                      <span
                        data-testid={`feature-illustrative-${f.id}`}
                        className="absolute right-3 top-3 z-10 rounded-sm border border-white/15 bg-loudar-ink/70 px-2 py-1 text-[9px] uppercase tracking-[0.22em] text-white/60 backdrop-blur"
                      >
                        Illustrative preview
                      </span>
                    </div>
                  </div>
                </motion.div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;
